import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Spinner } from '../../../../../common/index.jsx';
import { AppBarCMS } from '../../../../../basic/index.jsx';
import { routes } from '../../../../../../utils/helpers/ApiClient.jsx';
import {
  useGetInternalJobsQuery,
  useDeleteInternalJobMutation,
  useApproveInternalJobMutation,
  useRejectInternalJobMutation,
  useArchiveInternalJobMutation,
  useRestoreInternalJobMutation,
} from '../../../../../../redux/services/fij_internal_jobs.jsx';
import { useDeleteInterestMutation } from '../../../../../../redux/services/fij_applications.jsx';
import useUserHasRole from '../../../../../../hooks/useUserHasRoles.jsx';
import {
  ROLE_FRI_INTERNAL_JOBS_ADMIN,
  ROLE_FRI_INTERNAL_JOBS_LECTURER,
  ROLE_FRI_INTERNAL_JOBS_INTERN,
} from '../../../../../../utils/constants/roles.js';
import { useToastMessages } from '../../../../../../hooks/useToastMessages.jsx';
import { FIJ_JOB_STATUS } from '../../../../../../utils/constants/enums.js';
import TableFilter from '../../../../../common/TableFilter/TableFilter.jsx';
import FijJobsTable from './FijJobsTable.jsx';

const FijJobsPage = () => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.auth.user);
  const isFijAdmin = useUserHasRole(ROLE_FRI_INTERNAL_JOBS_ADMIN);
  const isFijLecturer = useUserHasRole(ROLE_FRI_INTERNAL_JOBS_LECTURER);
  const isFijIntern = useUserHasRole(ROLE_FRI_INTERNAL_JOBS_INTERN);

  const location = useLocation();

  const { data: internalJobs, isLoading, refetch } = useGetInternalJobsQuery();

  const [
    deleteInternalJob,
    { isSuccess: isDeleteSuccess, isError: isDeleteError, error: deleteError },
  ] = useDeleteInternalJobMutation();
  const [
    approveInternalJob,
    {
      isSuccess: isApproveSuccess,
      isError: isApproveError,
      error: approveError,
    },
  ] = useApproveInternalJobMutation();
  const [
    rejectInternalJob,
    { isSuccess: isRejectSuccess, isError: isRejectError, error: rejectError },
  ] = useRejectInternalJobMutation();
  const [
    archiveInternalJob,
    {
      isSuccess: isArchiveSuccess,
      isError: isArchiveError,
      error: archiveError,
    },
  ] = useArchiveInternalJobMutation();
  const [
    restoreInternalJob,
    {
      isSuccess: isRestoreSuccess,
      isError: isRestoreError,
      error: restoreError,
    },
  ] = useRestoreInternalJobMutation();
  const [
    deleteInterest,
    {
      isSuccess: isDeleteInterestSuccess,
      isError: isDeleteInterestError,
      error: deleteInterestError,
    },
  ] = useDeleteInterestMutation();

  const filerTitle = t('Filter FRI praxí');
  const pageTitle = t('Zoznam FRI praxí');

  const normalizedInternalJobs = useMemo(
    () =>
      Array.isArray(internalJobs)
        ? internalJobs
        : Array.isArray(internalJobs?.data)
          ? internalJobs.data
          : [],
    [internalJobs],
  );

  useEffect(() => {
    document.title = pageTitle + ' - FRI.prax';
  }, [pageTitle]); // Update when pageTitle changes (translation ready)

  const { successMessage, deleteSuccess, deleteFailed, apiErrorMessage } =
    useToastMessages();

  useEffect(() => {
    if (isDeleteSuccess) {
      deleteSuccess();
      refetch();
    }
    if (isDeleteError) {
      deleteFailed(deleteError);
    }
  }, [isDeleteSuccess, isDeleteError, deleteError]);

  useEffect(() => {
    if (isApproveSuccess) {
      successMessage({
        message: t('Téma FRI praxe bola schválená!'),
      });
      refetch();
    }
    if (isApproveError) {
      apiErrorMessage(approveError);
    }
  }, [isApproveSuccess, isApproveError, approveError]);

  useEffect(() => {
    if (isRejectSuccess) {
      successMessage({
        message: t('Téma FRI praxe bola zamietnutá!'),
      });
      refetch();
    }
    if (isRejectError) {
      apiErrorMessage(rejectError);
    }
  }, [isRejectSuccess, isRejectError, rejectError]);

  useEffect(() => {
    if (isArchiveSuccess) {
      successMessage({
        message: t('Téma FRI praxe bola archivovaná'),
      });
      refetch();
    }
    if (isArchiveError) {
      apiErrorMessage(archiveError);
    }
  }, [isArchiveSuccess, isArchiveError, archiveError]);

  useEffect(() => {
    if (isRestoreSuccess) {
      successMessage({
        message: t('Téma FRI praxe bola obnovená z archívu'),
      });
      refetch();
    }
    if (isRestoreError) {
      apiErrorMessage(restoreError);
    }
  }, [isRestoreSuccess, isRestoreError, restoreError]);

  useEffect(() => {
    if (isDeleteInterestSuccess) {
      deleteSuccess();
      refetch();
    }
    if (isDeleteInterestError) {
      deleteFailed(deleteInterestError);
    }
  }, [isDeleteInterestSuccess, isDeleteInterestError, deleteInterestError]);

  const internalJobsFilterOptions = useMemo(() => {
    const options = [];

    if (isFijLecturer) {
      options.push({ value: 'mine', label: 'Moje praxe' });
    }

    options.push({ value: 'current', label: 'Aktívne praxe' });

    if (isFijAdmin) {
      const waitingCount = normalizedInternalJobs.filter(
        (job) => job.status === FIJ_JOB_STATUS.WAITING && !job.archived_at,
      ).length;
      options.push({
        value: 'waiting',
        label: 'Čakajúce na schválenie',
        count: waitingCount,
      });
    }

    options.push({ value: 'archived', label: 'Archivované praxe' });

    return options;
  }, [normalizedInternalJobs, isFijLecturer, isFijAdmin]);

  const hasForApprovalParam = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.has('forApproval');
  }, [location.search]);

  const [selectedInternalJobsFilter, setSelectedInternalJobsFilter] = useState(
    () => {
      if (isFijIntern) {
        return 'current';
      }

      if (hasForApprovalParam && isFijAdmin) {
        return 'waiting';
      }

      const stored = localStorage.getItem(filerTitle);
      const storedIsValid =
        stored &&
        internalJobsFilterOptions.some((option) => option.value === stored);

      return storedIsValid
        ? stored
        : internalJobsFilterOptions[0]?.value || 'current';
    },
  );

  useEffect(() => {
    if (isFijIntern) {
      setSelectedInternalJobsFilter('current');
      return;
    }

    // Ak je aktuálna hodnota neplatná (napr. zmena rolí), zvoľ rozumný fallback.
    if (
      !internalJobsFilterOptions.some(
        (option) => option.value === selectedInternalJobsFilter,
      )
    ) {
      const fallback =
        hasForApprovalParam && isFijAdmin
          ? 'waiting'
          : internalJobsFilterOptions[0]?.value || 'current';

      setSelectedInternalJobsFilter(fallback);
    }
  }, [
    internalJobsFilterOptions,
    selectedInternalJobsFilter,
    isFijIntern,
    hasForApprovalParam,
    isFijAdmin,
  ]);

  useEffect(() => {
    if (isFijIntern) return;
    localStorage.setItem(filerTitle, selectedInternalJobsFilter);
  }, [selectedInternalJobsFilter, filerTitle, isFijIntern]);

  return isLoading ? (
    <Spinner />
  ) : (
    <>
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsDashboard}
        backUrlTooltip={t('Nástenka')}
        createText={t('Pridať FRI prax')}
        createUrl={routes.cmsFijJobCreate}
        permissionsBasic={'fri-internal-jobs-jobs'}
      />

      <div style={{ margin: '10px' }}>
        {!isFijIntern && (
          <TableFilter
            title={filerTitle}
            value={selectedInternalJobsFilter}
            setValue={setSelectedInternalJobsFilter}
            options={internalJobsFilterOptions}
          />
        )}

        <FijJobsTable
          user={user}
          internalJobs={normalizedInternalJobs}
          selectedView={selectedInternalJobsFilter}
          onSelectedViewChange={setSelectedInternalJobsFilter}
          isFijAdmin={isFijAdmin}
          isFijLecturer={isFijLecturer}
          isFijIntern={isFijIntern}
          onApproveClick={approveInternalJob}
          onRejectClick={rejectInternalJob}
          onArchiveClick={archiveInternalJob}
          onRestoreClick={(id, autoApprove) =>
            restoreInternalJob({ id, autoApprove })
          }
          onDeleteClick={deleteInternalJob}
          onDeleteInterestClick={deleteInterest}
          fromPublic={false}
        />
      </div>
    </>
  );
};

export default FijJobsPage;
